/* ============================================================
   TransferLabs — Custom Shirt Builder

   What this is
     A visual product configurator. Customer picks color + size,
     uploads artwork for the front and/or back of the shirt, and
     drags/resizes each artwork inside a dashed "print box" that
     overlays the mockup. One shirt configuration = one line item.

   Coordinate systems
     • Print-box position on mockup = PERCENT of the mockup image
       (from section settings, so it's mockup-image-agnostic).
     • Artwork position within the print box = PERCENT of the box
       (0..100 for x, y, w, h). Stored this way so the same layout
       survives a color swap that reloads mockups of different pixel
       dimensions.
     • For admin _Layout JSON we convert those percentages to inches
       using the section's real print-size settings (front_in_w/h,
       back_in_w/h) so production knows how big to render.

   Cart submit
     Single POST to /cart/add. Multipart form data carries:
       • Front Artwork / Back Artwork  (uploaded original files)
       • Front Preview / Back Preview  (client-side composite PNG)
       • Color, Size                   (customer-visible)
       • _Layout, _Tier At Add         (admin-only, underscore prefix)
   ============================================================ */

(function () {
  'use strict';

  const MAX_MB_HARD = 20;              // Shopify hard cap for line-item file props
  const MIN_ART_PCT = 15;              // artwork can't be smaller than 15% of print box
  const EXPORT_PX_LONG_EDGE = 1400;    // composite PNG target size

  /* ── Helpers ─────────────────────────────────────────────── */
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const round2 = (n) => Math.round(n * 100) / 100;
  const fmtBytes = (b) =>
    b < 1024 ? `${b} B` :
    b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` :
    `${(b / 1024 / 1024).toFixed(2)} MB`;
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c =>
    ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));

  function fileKind(file) {
    const t = file.type || '', n = (file.name || '').toLowerCase();
    if (/^image\/(png|jpe?g)/.test(t) || /\.(png|jpe?g)$/.test(n)) return 'raster';
    if (/svg/.test(t) || /\.svg$/.test(n)) return 'svg';
    return 'file';
  }

  function initBuilder(root) {
    if (!root || root.dataset.tlShirtReady === '1') return;
    root.dataset.tlShirtReady = '1';

    /* ── Config ────────────────────────────────────────────── */
    const CURRENCY = root.dataset.currency || 'USD';
    const CART_ADD = root.dataset.cartAddUrl || '/cart/add';
    const CART_URL = root.dataset.cartUrl || '/cart';
    const CART_JS  = root.dataset.cartJsUrl || '/cart.js';
    const PRODUCT_ID = parseInt(root.dataset.productId, 10) || 0;
    const MAX_MB = Math.min(parseFloat(root.dataset.maxMb) || MAX_MB_HARD, MAX_MB_HARD);
    const DPI = parseFloat(root.dataset.dpi) || 300;

    const MIN_QTY = parseInt(root.dataset.minQty, 10) || 12;
    const PRINT = {
      front: safeJSON(root.dataset.printFront, { x: 30, y: 22, w: 40, h: 50, inW: 13.5, inH: 15.5 }),
      back:  safeJSON(root.dataset.printBack,  { x: 30, y: 20, w: 40, h: 50, inW: 13.5, inH: 15.5 }),
    };

    const colors   = safeJSON(root.querySelector('[data-tl-shirt-colors]')?.textContent, []);
    const variants = safeJSON(root.querySelector('[data-tl-shirt-variants]')?.textContent, []);

    /* ── DOM refs ──────────────────────────────────────────── */
    const q = (sel) => root.querySelector(sel);
    const qAll = (sel) => Array.from(root.querySelectorAll(sel));

    const $sideTabs   = qAll('[data-tl-shirt-side]');
    const $sideBadges = { front: q('[data-tl-shirt-side-badge="front"]'), back: q('[data-tl-shirt-side-badge="back"]') };
    const $mockup     = q('[data-tl-shirt-mockup]');
    const $mockupWrap = q('[data-tl-shirt-mockup-wrap]');
    const $printBox   = q('[data-tl-shirt-printbox]');
    const $printLabel = q('[data-tl-shirt-printbox-label]');
    const $art        = q('[data-tl-shirt-art]');
    const $artImg     = q('[data-tl-shirt-art-img]');
    const $artWVal    = q('[data-tl-shirt-art-w-val]');
    const $artHVal    = q('[data-tl-shirt-art-h-val]');
    const $handles    = qAll('[data-tl-shirt-handle]');
    const $emptyMsg   = q('[data-tl-shirt-empty]');
    const $stage      = q('[data-tl-shirt-stage]');
    const $hint       = q('[data-tl-shirt-hint]');

    const $tools = {
      fit:    q('[data-tl-shirt-action="fit"]'),
      center: q('[data-tl-shirt-action="center"]'),
      rotate: q('[data-tl-shirt-action="rotate"]'),
      remove: q('[data-tl-shirt-action="remove"]'),
    };

    const $swatches = q('[data-tl-shirt-swatches]');
    const $colorName = q('[data-tl-shirt-color-name]');
    const $activeColor = q('[data-tl-shirt-active-color]');
    const $sizes = q('[data-tl-shirt-sizes]');
    const $orderBlock = q('[data-tl-shirt-order-block]');
    const $orderList = q('[data-tl-shirt-order-list]');
    const $orderTotalQty = q('[data-tl-shirt-order-total-qty]');

    const $uploadTabs = qAll('[data-tl-shirt-upload-tab]');
    const $uploadSideLabel = q('[data-tl-shirt-upload-side]');
    const $uploadHint = q('[data-tl-shirt-upload-hint]');
    const $fileInput  = q('[data-tl-shirt-file]');
    const $drop       = q('[data-tl-shirt-drop]');
    const $upEmpty    = q('[data-tl-shirt-upload-empty]');
    const $upFilled   = q('[data-tl-shirt-upload-filled]');
    const $upThumbImg = q('[data-tl-shirt-upload-thumb-img]');
    const $upBadge    = q('[data-tl-shirt-upload-badge]');
    const $upName     = q('[data-tl-shirt-upload-name]');
    const $upSize     = q('[data-tl-shirt-upload-size]');
    const $upReplace  = q('[data-tl-shirt-upload-replace]');
    const $upRemove   = q('[data-tl-shirt-upload-remove]');
    const $upErr      = q('[data-tl-shirt-upload-err]');

    // Min-order progress card refs (replaces cumulative tier UI)
    const $minCount  = q('[data-tl-shirt-minorder-count]');
    const $minMsg    = q('[data-tl-shirt-minorder-msg]');
    const $minNeed   = q('[data-tl-shirt-minorder-need]');
    const $minCheck  = q('[data-tl-shirt-minorder-check]');
    const $minFill   = q('[data-tl-shirt-minorder-fill]');

    const $printRow  = q('[data-tl-shirt-print-row]');
    const $printMode = q('[data-tl-shirt-print-mode]');
    const $sumLabel  = q('[data-tl-shirt-sum-label]');
    const $sumLine   = q('[data-tl-shirt-sum-line]');
    const $total     = q('[data-tl-shirt-total]');

    // Header two-tier price display
    const $pricebar    = q('[data-tl-shirt-pricebar]');
    const $priceOne    = q('[data-tl-shirt-price-one]');
    const $priceBoth   = q('[data-tl-shirt-price-both]');
    const $priceTiers  = qAll('[data-tl-shirt-pricebar-tier]');
    const $priceDivider = q('.tl-shirt__pricebar-plus');

    // Back-print upsell cards
    const $upsell         = q('[data-tl-shirt-upsell]');
    const $upsellHeadline = q('[data-tl-shirt-upsell-headline]');
    const $upsellCurrent  = q('[data-tl-shirt-upsell-current]');
    const $upsellCta      = q('[data-tl-shirt-upsell-cta]');
    const $upsellCtaLabel = q('[data-tl-shirt-upsell-cta-label]');
    const $upsellMet      = q('[data-tl-shirt-upsell-met]');
    const $upsellMetPrice = q('[data-tl-shirt-upsell-met-price]');

    const $atc       = q('[data-tl-shirt-submit]');
    const $atcLabel  = q('[data-tl-shirt-atc-label]');
    const $err       = q('[data-tl-shirt-error]');

    /* ── State ─────────────────────────────────────────────── */
    const state = {
      activeSide: 'front',       // which side the canvas + upload tab is showing
      activeColor: colors[0]?.name || '',   // which color's mockup is loaded / matrix visible
      qtyMatrix: {},             // { "Color|Size": qty }, only non-zero entries kept
      existingCartQty: 0,
      // Per-side artwork state. Positions stored as % of print box (0..100).
      sides: {
        front: emptyArt(),
        back:  emptyArt(),
      },
    };

    /* Matrix helpers */
    const matrixKey = (color, size) => `${color}|${size}`;
    function setMatrixQty(color, size, qty) {
      const k = matrixKey(color, size);
      if (qty > 0) state.qtyMatrix[k] = qty;
      else delete state.qtyMatrix[k];
    }
    function getMatrixQty(color, size) {
      return state.qtyMatrix[matrixKey(color, size)] || 0;
    }
    function totalQtyForColor(color) {
      return Object.entries(state.qtyMatrix)
        .filter(([k]) => k.startsWith(color + '|'))
        .reduce((s, [, q]) => s + q, 0);
    }
    function totalQtyAll() {
      return Object.values(state.qtyMatrix).reduce((s, q) => s + q, 0);
    }
    function matrixLines() {
      // { color, size, qty, variant } for every non-zero entry.
      // Variant lookup depends on how many sides have artwork right now —
      // that's what drives one-side vs both-sides pricing.
      const sides = printSides();
      return Object.entries(state.qtyMatrix).map(([k, qty]) => {
        const [color, size] = k.split('|');
        return {
          color,
          size,
          qty,
          variant: sides ? findVariant(color, size, sides) : null,
        };
      }).sort((a, b) => a.color.localeCompare(b.color) || a.size.localeCompare(b.size));
    }

    function emptyArt() {
      return {
        file: null, fileName: '', fileSize: 0, fileURL: null, fileKind: null,
        img: null, imgW: 0, imgH: 0,
        xPct: 10, yPct: 10, wPct: 80, hPct: 80,  // start 80% centered
        rotation: 0,
      };
    }

    /* ── Formatting ───────────────────────────────────────── */
    const money = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    const fmt = (cents) => money.format((cents || 0) / 100);

    /* ── Variant lookup ───────────────────────────────────── */
    // Product options: 1 = Color, 2 = Size, 3 = Print ("One Side" | "Both Sides")
    // When the customer has designed both sides we pick the "Both Sides" variant
    // (priced $22 in Shopify Admin); otherwise the "One Side" variant ($18).
    function findVariant(color, size, sides) {
      return variants.find(v =>
        v.option1 === color &&
        v.option2 === size &&
        v.option3 === sides &&
        v.available !== false
      ) || variants.find(v =>
        v.option1 === color && v.option2 === size && v.option3 === sides
      );
    }
    function anyAvailableForColor(color) {
      return variants.some(v => v.option1 === color && v.available !== false);
    }
    function anyAvailableForSize(color, size) {
      return variants.some(v => v.option1 === color && v.option2 === size && v.available !== false);
    }
    function uniqueSizesForColor(color) {
      const seen = new Set(), out = [];
      variants.filter(v => v.option1 === color).forEach(v => {
        if (!seen.has(v.option2)) { seen.add(v.option2); out.push(v.option2); }
      });
      return out;
    }
    function minPrice() {
      // "From $XX" header — cheapest available variant across the whole matrix
      const avail = variants.filter(v => v.available !== false);
      if (!avail.length) return variants[0]?.price || 0;
      return Math.min.apply(null, avail.map(v => v.price));
    }
    // Cheapest variant priced for a specific option3 ("One Side" / "Both Sides").
    function minPriceForSides(sides) {
      const filtered = variants.filter(v => v.option3 === sides && v.available !== false);
      if (!filtered.length) return 0;
      return Math.min.apply(null, filtered.map(v => v.price));
    }

    /* ── Print sides (matches option3 on the shirt variants) ── */
    // Returns the exact option3 string ("One Side" / "Both Sides") so we can
    // hand it straight to findVariant() and get correct pricing back.
    function printSides() {
      const hasFront = !!state.sides.front.file;
      const hasBack  = !!state.sides.back.file;
      if (hasFront && hasBack) return 'Both Sides';
      if (hasFront || hasBack) return 'One Side';
      return null;
    }
    // Which specific side has art? Only for the summary label ("Front only" vs
    // "Back only") — pricing is the same either way.
    function whichOneSide() {
      if (state.sides.front.file && !state.sides.back.file) return 'front';
      if (state.sides.back.file && !state.sides.front.file) return 'back';
      return null;
    }

    /* ── Existing cart qty for this product ───────────────── */
    function fetchExistingCartQty() {
      if (!PRODUCT_ID) return Promise.resolve(0);
      return fetch(CART_JS, { headers: { Accept: 'application/json' } })
        .then(r => r.ok ? r.json() : { items: [] })
        .then(cart => (cart.items || [])
          .filter(it => it.product_id === PRODUCT_ID)
          .reduce((s, it) => s + (it.quantity || 0), 0))
        .catch(() => 0);
    }

    /* ── Swatches + size matrix ───────────────────────────── */
    function paintSwatches() {
      if (!colors.length) { $swatches.innerHTML = ''; return; }
      $swatches.innerHTML = colors.map(c => {
        const active = c.name === state.activeColor ? ' is-active' : '';
        const unav = anyAvailableForColor(c.name) ? '' : ' is-unavailable';
        const qty = totalQtyForColor(c.name);
        const badge = qty > 0
          ? `<span class="tl-shirt__swatch-qty">${qty}</span>`
          : '';
        return `<button type="button" class="tl-shirt__swatch${active}${unav}"
                   data-tl-shirt-color="${escapeHtml(c.name)}"
                   aria-pressed="${c.name === state.activeColor}"
                   style="background:${escapeHtml(c.swatch || '#ccc')};"
                   title="${escapeHtml(c.name)}${qty > 0 ? ' — ' + qty + ' in order' : ''}">
                  <span class="tl-shirt__swatch-name">${escapeHtml(c.name)}</span>
                  ${badge}
                </button>`;
      }).join('');
      if ($colorName) $colorName.textContent = state.activeColor || '—';
      if ($activeColor) $activeColor.textContent = state.activeColor || '—';
    }

    function paintSizeMatrix() {
      const sizes = uniqueSizesForColor(state.activeColor);
      if (!sizes.length) {
        $sizes.innerHTML = '<span class="tl-shirt__hint">No sizes available for this color.</span>';
        return;
      }
      $sizes.innerHTML = sizes.map(s => {
        const available = anyAvailableForSize(state.activeColor, s);
        const qty = getMatrixQty(state.activeColor, s);
        const disabled = available ? '' : 'disabled';
        const unavailCls = available ? '' : ' is-unavailable';
        return `
          <div class="tl-shirt__size-row${unavailCls}" data-size="${escapeHtml(s)}">
            <span class="tl-shirt__size-label">${escapeHtml(s)}</span>
            <div class="tl-shirt__size-step">
              <button type="button" data-tl-shirt-matrix-step="-1" aria-label="Decrease ${escapeHtml(s)}" ${disabled}>−</button>
              <input type="number" inputmode="numeric" min="0" step="1" value="${qty}" data-tl-shirt-matrix-input ${disabled}>
              <button type="button" data-tl-shirt-matrix-step="1" aria-label="Increase ${escapeHtml(s)}" ${disabled}>+</button>
            </div>
            ${available ? '' : '<span class="tl-shirt__size-oos">Sold out</span>'}
          </div>
        `;
      }).join('');
    }

    function paintOrderList() {
      const lines = matrixLines();
      if (!lines.length) {
        // Belt-and-suspenders: clear the list DOM too so stale rows can't
        // linger even if a stylesheet overrides [hidden].
        $orderList.innerHTML = '';
        $orderBlock.hidden = true;
        return;
      }
      $orderBlock.hidden = false;
      $orderList.innerHTML = lines.map(l => {
        const price = l.variant ? fmt(l.variant.price * l.qty) : '—';
        return `
          <li class="tl-shirt__order-row">
            <span class="tl-shirt__order-swatch" style="background:${escapeHtml((colors.find(c=>c.name===l.color)||{}).swatch || '#ccc')};" aria-hidden="true"></span>
            <span class="tl-shirt__order-name">${escapeHtml(l.color)} · ${escapeHtml(l.size)}</span>
            <span class="tl-shirt__order-qty">Qty ${l.qty}</span>
            <span class="tl-shirt__order-price">${price}</span>
            <button type="button" class="tl-shirt__order-remove" data-tl-shirt-remove-line="${escapeHtml(l.color)}|${escapeHtml(l.size)}" aria-label="Remove ${escapeHtml(l.color)} ${escapeHtml(l.size)}">×</button>
          </li>
        `;
      }).join('');
      $orderTotalQty.textContent = totalQtyAll();
    }

    /* ── Mockup + print box positioning ───────────────────── */
    function currentColorData() {
      return colors.find(c => c.name === state.activeColor) || colors[0] || null;
    }

    function loadMockup() {
      const c = currentColorData();
      if (!c) return;
      const url = state.activeSide === 'back' ? c.back : c.front;
      if (url) {
        $mockup.src = url;
        $mockup.hidden = false;
      } else {
        $mockup.removeAttribute('src');
        $mockup.hidden = true;
      }
    }

    function positionPrintBox() {
      const p = PRINT[state.activeSide] || PRINT.front;
      $printBox.style.left   = p.x + '%';
      $printBox.style.top    = p.y + '%';
      $printBox.style.width  = p.w + '%';
      // Height is derived from real print inches via CSS aspect-ratio,
      // so the on-screen box shape matches what will actually be printed.
      // (Setting height to '' clears any leftover inline height.)
      $printBox.style.height = '';
      root.style.setProperty('--tl-shirt-print-aspect', `${p.inW} / ${p.inH}`);
      $printLabel.textContent = `${p.inW}″ × ${p.inH}″ print area`;
    }

    /* ── Artwork positioning ──────────────────────────────── */
    function activeArt() { return state.sides[state.activeSide]; }

    function positionArt() {
      const art = activeArt();
      if (!art.file) { $art.hidden = true; return; }
      const p = PRINT[state.activeSide];
      // Read the print-box's real on-screen pixel dims. CSS aspect-ratio
      // has already made the box's height match the real inW/inH ratio,
      // so % values of the box correspond 1:1 to real-inch proportions.
      const boxRect = $printBox.getBoundingClientRect();
      const wrapRect = $mockupWrap.getBoundingClientRect();
      if (!wrapRect.width || !wrapRect.height) return;

      const artPxL = (boxRect.left - wrapRect.left) + (art.xPct / 100) * boxRect.width;
      const artPxT = (boxRect.top  - wrapRect.top)  + (art.yPct / 100) * boxRect.height;
      const artPxW = (art.wPct / 100) * boxRect.width;
      const artPxH = (art.hPct / 100) * boxRect.height;

      $art.style.left   = (artPxL / wrapRect.width  * 100) + '%';
      $art.style.top    = (artPxT / wrapRect.height * 100) + '%';
      $art.style.width  = (artPxW / wrapRect.width  * 100) + '%';
      $art.style.height = (artPxH / wrapRect.height * 100) + '%';
      $art.style.transform = art.rotation ? `rotate(${art.rotation}deg)` : '';
      $artImg.src = art.fileURL || '';
      $art.hidden = false;

      // Live real-world inches on both axes (matches what production will print)
      const wIn = (art.wPct / 100) * p.inW;
      const hIn = (art.hPct / 100) * p.inH;
      if ($artWVal) $artWVal.textContent = `${wIn.toFixed(1)}″`;
      if ($artHVal) $artHVal.textContent = `${hIn.toFixed(1)}″`;
    }

    function paintStage() {
      loadMockup();
      positionPrintBox();
      positionArt();
      const art = activeArt();
      const hasArt = !!art.file;
      $emptyMsg.hidden = hasArt;
      $hint.hidden = !hasArt;
      Object.values($tools).forEach(btn => btn && (btn.disabled = !hasArt));

      $sideBadges.front && ($sideBadges.front.hidden = !state.sides.front.file);
      $sideBadges.back  && ($sideBadges.back.hidden  = !state.sides.back.file);
    }

    /* ── Upload UI (per side) ─────────────────────────────── */
    function paintUploadUI() {
      const art = activeArt();
      if ($uploadSideLabel) $uploadSideLabel.textContent = state.activeSide;
      if ($uploadHint) $uploadHint.textContent = art.file ? 'Uploaded ✓' : 'Not uploaded';
      $uploadTabs.forEach(btn => {
        const on = btn.dataset.tlShirtUploadTab === state.activeSide;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      if (art.file) {
        $upEmpty.hidden = true;
        $upFilled.hidden = false;
        $upName.textContent = art.fileName;
        $upSize.textContent = fmtBytes(art.fileSize);
        if (art.fileKind === 'raster' && art.fileURL) {
          $upThumbImg.src = art.fileURL;
          $upThumbImg.hidden = false;
          $upBadge.hidden = true;
        } else {
          $upThumbImg.hidden = true;
          $upThumbImg.removeAttribute('src');
          $upBadge.hidden = false;
          $upBadge.textContent = art.fileKind === 'svg' ? 'SVG' : 'FILE';
        }
      } else {
        $upEmpty.hidden = false;
        $upFilled.hidden = true;
        // Also clear the filled fields so if the CSS-hidden guard ever slips
        // (e.g. third-party stylesheet override), stale data from the other
        // side doesn't linger visually.
        $upName.textContent = '—';
        $upSize.textContent = '—';
        $upThumbImg.hidden = true;
        $upThumbImg.removeAttribute('src');
        $upBadge.hidden = true;
      }
    }

    function paintSideTabs() {
      $sideTabs.forEach(btn => {
        const on = btn.dataset.tlShirtSide === state.activeSide;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    /* ── File handling ────────────────────────────────────── */
    function attachFile(file) {
      const art = activeArt();
      if (!file) return;

      if (file.size > MAX_MB * 1024 * 1024) {
        showUploadErr(`File is ${fmtBytes(file.size)}. Max ${MAX_MB}MB.`);
        return;
      }
      clearUploadErr();

      // Revoke previous URL
      if (art.fileURL) try { URL.revokeObjectURL(art.fileURL); } catch (e) {}

      art.file = file;
      art.fileName = file.name;
      art.fileSize = file.size;
      art.fileKind = fileKind(file);
      art.fileURL = (art.fileKind === 'raster' || art.fileKind === 'svg') ? URL.createObjectURL(file) : null;

      // Reset placement to a nicely centered 60% width (aspect from image if raster)
      if (art.fileKind === 'raster' && art.fileURL) {
        const probe = new Image();
        probe.onload = () => {
          art.imgW = probe.naturalWidth;
          art.imgH = probe.naturalHeight;
          // Fit inside print box maintaining aspect ratio
          fitArtToBox(art);
          if (activeArt() === art) paintAll();
        };
        probe.onerror = () => {
          fitArtToBox(art);
          if (activeArt() === art) paintAll();
        };
        probe.src = art.fileURL;
      } else {
        // No aspect info — assume square-ish
        art.imgW = 1; art.imgH = 1;
        fitArtToBox(art);
      }
      paintAll();
    }

    function fitArtToBox(art) {
      const p = PRINT[state.activeSide];
      // Box on screen now matches real inW×inH aspect, so use that directly.
      const boxAspect = p.inW / p.inH;
      const artAspect = (art.imgW || 1) / (art.imgH || 1);
      let wPct, hPct;
      if (artAspect >= boxAspect) {
        wPct = 100;
        hPct = clamp((100 * boxAspect) / artAspect, MIN_ART_PCT, 100);
      } else {
        hPct = 100;
        wPct = clamp((100 * artAspect) / boxAspect, MIN_ART_PCT, 100);
      }
      art.wPct = wPct;
      art.hPct = hPct;
      art.xPct = (100 - wPct) / 2;
      art.yPct = (100 - hPct) / 2;
      art.rotation = 0;
    }

    // Pixel aspect ratio of the mockup — needed so wPct/hPct produce visually
    // correct shapes when the print box % is non-square.
    function mockupPxAspect() {
      const box = $mockupWrap.getBoundingClientRect();
      if (!box.width || !box.height) return 1;
      return box.width / box.height;
    }

    function centerArt() {
      const art = activeArt();
      art.xPct = (100 - art.wPct) / 2;
      art.yPct = (100 - art.hPct) / 2;
      positionArt();
    }
    function rotateArt() {
      const art = activeArt();
      art.rotation = (art.rotation + 90) % 360;
      positionArt();
    }
    function removeArt() {
      const art = activeArt();
      if (art.fileURL) try { URL.revokeObjectURL(art.fileURL); } catch (e) {}
      state.sides[state.activeSide] = emptyArt();
      clearUploadErr();
      paintAll();
    }

    /* ── Drag & resize (pointer events) ───────────────────── */
    let pointerOp = null;   // { mode: 'move'|'resize', corner, startX, startY, startArt }
    function onPointerDown(e) {
      const art = activeArt();
      if (!art.file) return;

      const handle = e.target.closest('[data-tl-shirt-handle]');
      const inArt  = e.target.closest('[data-tl-shirt-art]');
      if (!handle && !inArt) return;

      e.preventDefault();
      $art.setPointerCapture?.(e.pointerId);
      $art.classList.add('is-dragging', 'is-active');
      pointerOp = {
        mode: handle ? 'resize' : 'move',
        corner: handle ? handle.dataset.tlShirtHandle : null,
        startX: e.clientX,
        startY: e.clientY,
        startArt: { xPct: art.xPct, yPct: art.yPct, wPct: art.wPct, hPct: art.hPct },
      };
    }
    function onPointerMove(e) {
      if (!pointerOp) return;
      const art = activeArt();
      // Use the real print-box pixel size (CSS aspect-ratio derives height
      // from width, so this is guaranteed to match the real-inches shape).
      const boxRect = $printBox.getBoundingClientRect();
      const boxPxW = boxRect.width;
      const boxPxH = boxRect.height;
      if (!boxPxW || !boxPxH) return;

      const dxPct = ((e.clientX - pointerOp.startX) / boxPxW) * 100;
      const dyPct = ((e.clientY - pointerOp.startY) / boxPxH) * 100;
      const s = pointerOp.startArt;

      if (pointerOp.mode === 'move') {
        art.xPct = clamp(s.xPct + dxPct, 0, 100 - s.wPct);
        art.yPct = clamp(s.yPct + dyPct, 0, 100 - s.hPct);
      } else {
        // Resize from grabbed corner, maintaining aspect ratio via smaller delta.
        const aspect = s.wPct / s.hPct;
        let newW = s.wPct, newH = s.hPct, newX = s.xPct, newY = s.yPct;
        const c = pointerOp.corner;

        // Convert horizontal delta into a proposed width change,
        // then match height via the fixed aspect so the corner tracks
        // the pointer diagonally without shearing the image.
        let deltaW = 0;
        if (c === 'se' || c === 'ne') deltaW =  dxPct;
        if (c === 'sw' || c === 'nw') deltaW = -dxPct;
        newW = clamp(s.wPct + deltaW, MIN_ART_PCT, 100);
        newH = clamp(newW / aspect, MIN_ART_PCT, 100);
        // Re-clamp width if height hit the ceiling/floor
        newW = newH * aspect;

        // Anchor the opposite corner so resize feels natural.
        if (c === 'se') { newX = s.xPct; newY = s.yPct; }
        if (c === 'sw') { newX = s.xPct + (s.wPct - newW); newY = s.yPct; }
        if (c === 'ne') { newX = s.xPct; newY = s.yPct + (s.hPct - newH); }
        if (c === 'nw') { newX = s.xPct + (s.wPct - newW); newY = s.yPct + (s.hPct - newH); }

        // Clamp inside the print box.
        newX = clamp(newX, 0, 100 - newW);
        newY = clamp(newY, 0, 100 - newH);
        art.xPct = newX; art.yPct = newY; art.wPct = newW; art.hPct = newH;
      }
      positionArt();
    }
    function onPointerUp(e) {
      if (!pointerOp) return;
      try { $art.releasePointerCapture?.(e.pointerId); } catch (_) {}
      $art.classList.remove('is-dragging');
      pointerOp = null;
    }

    /* ── Header pricebar: shows both tier prices, highlights active tier ── */
    function paintPricebar() {
      const oneCents  = minPriceForSides('One Side');
      const bothCents = minPriceForSides('Both Sides');
      if ($priceOne)  $priceOne.textContent  = oneCents  ? fmt(oneCents)  : '—';
      if ($priceBoth) $priceBoth.textContent = bothCents ? fmt(bothCents) : '—';

      // Divider chip shows the actual delta (+$4) so it's honest
      if ($priceDivider && oneCents && bothCents) {
        $priceDivider.textContent = `+${fmt(bothCents - oneCents)}`;
      }

      // Highlight the tier the customer is currently on (based on artwork)
      const sides = printSides();
      $priceTiers.forEach(t => {
        t.classList.toggle('is-active', sides && t.dataset.tlShirtPricebarTier === sides);
      });
    }

    /* ── Back-print upsell prompt ──────────────────────────── */
    function paintUpsell() {
      if (!$upsell || !$upsellMet) return;
      const hasFront = !!state.sides.front.file;
      const hasBack  = !!state.sides.back.file;
      const oneCents  = minPriceForSides('One Side');
      const bothCents = minPriceForSides('Both Sides');
      const delta = (bothCents - oneCents) || 0;

      // Both sides designed → show confirmation card, hide upsell prompt
      if (hasFront && hasBack) {
        $upsell.hidden = true;
        $upsellMet.hidden = false;
        if ($upsellMetPrice) $upsellMetPrice.textContent = `${fmt(bothCents)}/shirt`;
        return;
      }

      // Exactly one side → show upsell prompt tuned to the empty side
      if (hasFront || hasBack) {
        $upsellMet.hidden = true;
        $upsell.hidden = false;
        const emptySide = hasFront ? 'back' : 'front';
        const currentSide = hasFront ? 'Front' : 'Back';
        if ($upsellHeadline) {
          $upsellHeadline.textContent = `Add a ${emptySide} print for +${fmt(delta)}/shirt`;
        }
        if ($upsellCurrent) {
          $upsellCurrent.textContent = `${currentSide} only · ${fmt(oneCents)}/shirt`;
        }
        if ($upsellCtaLabel) {
          $upsellCtaLabel.textContent = `Design the ${emptySide} →`;
        }
        if ($upsellCta) $upsellCta.dataset.tlUpsellTarget = emptySide;
        return;
      }

      // No art at all → hide both
      $upsell.hidden = true;
      $upsellMet.hidden = true;
    }

    /* ── Min-order progress card ───────────────────────────── */
    function paintMinOrder() {
      const orderQty = totalQtyAll();
      const need = Math.max(0, MIN_QTY - orderQty);
      const pct = Math.min(100, Math.round((orderQty / MIN_QTY) * 100));

      $minCount.textContent = `${orderQty} of ${MIN_QTY} shirt${MIN_QTY === 1 ? '' : 's'}`;
      $minFill.style.width = pct + '%';

      if (need > 0) {
        $minMsg.hidden = false;
        $minCheck.hidden = true;
        $minNeed.textContent = String(need);
        // Rewrite text so we can update the number cleanly
        $minMsg.innerHTML =
          `Add <em data-tl-shirt-minorder-need>${need}</em> more to reach the ${MIN_QTY}-shirt minimum`;
      } else {
        $minMsg.hidden = true;
        $minCheck.hidden = false;
      }

      root.querySelector('[data-tl-shirt-minorder]')
        ?.classList.toggle('is-met', need === 0 && orderQty > 0);
    }

    /* ── Summary + ATC ─────────────────────────────────────── */
    function paintSummary() {
      const lines = matrixLines();
      const hasArt = state.sides.front.file || state.sides.back.file;
      const orderQty = totalQtyAll();
      const sides = printSides();

      // Variant pricing (option3) already reflects one-side vs both-sides,
      // so subtotal is just sum of variant.price × qty across all lines.
      let subtotalCents = 0;
      let allValid = true;
      lines.forEach(l => {
        if (l.variant) subtotalCents += l.variant.price * l.qty;
        else allValid = false;
      });

      // Print sides summary row
      if (sides) {
        $printRow.hidden = false;
        const one = whichOneSide();
        const label =
          sides === 'Both Sides' ? 'Front + Back' :
          one === 'front'        ? 'Front only'   :
          one === 'back'         ? 'Back only'    :
          'One Side';
        $printMode.textContent = label;
      } else {
        $printRow.hidden = true;
      }

      if (orderQty > 0 && allValid) {
        const uniqueColors = new Set(lines.map(l => l.color)).size;
        const colorPart = uniqueColors === 1 ? lines[0].color : `${uniqueColors} colors`;
        $sumLabel.textContent = `${orderQty} shirt${orderQty === 1 ? '' : 's'} · ${colorPart}`;
        $sumLine.textContent = fmt(subtotalCents);
        $total.textContent = fmt(subtotalCents);
      } else {
        $sumLabel.textContent = 'Set a quantity to see pricing';
        $sumLine.textContent = '—';
        $total.textContent = '—';
      }

      // (Header pricing is now painted by paintPricebar() — no lead-price ref here.)

      // ATC state — gated by artwork, min qty, and variant availability
      const meetsMin = orderQty >= MIN_QTY;
      const canBuy = orderQty > 0 && hasArt && allValid && meetsMin;
      $atc.disabled = !canBuy;

      if (!hasArt) {
        $atcLabel.textContent = 'Upload artwork for at least one side';
      } else if (orderQty === 0) {
        $atcLabel.textContent = 'Set a quantity for at least one size';
      } else if (!allValid) {
        $atcLabel.textContent = 'Some sizes are unavailable';
      } else if (!meetsMin) {
        const need = MIN_QTY - orderQty;
        $atcLabel.textContent = `Add ${need} more shirt${need === 1 ? '' : 's'} to check out`;
      } else {
        const label = lines.length === 1
          ? `Add ${orderQty} to Cart — ${fmt(subtotalCents)}`
          : `Add ${orderQty} shirts (${lines.length} sizes) — ${fmt(subtotalCents)}`;
        $atcLabel.textContent = label;
      }
    }

    function paintAll() {
      paintSideTabs();
      paintSwatches();
      paintSizeMatrix();
      paintOrderList();
      paintStage();
      paintUploadUI();
      paintMinOrder();
      paintPricebar();
      paintUpsell();
      paintSummary();
    }

    /* ── Composite PNG render (front / back separately) ─── */
    // We reload each side's mockup PNG and draw the artwork inside the
    // print box at export resolution. Returns a Promise<Blob>.
    function renderComposite(side) {
      return new Promise((resolve) => {
        const art = state.sides[side];
        const c   = currentColorData();
        const url = side === 'back' ? c?.back : c?.front;
        if (!art.file || !url) { resolve(null); return; }

        const mockImg = new Image();
        mockImg.crossOrigin = 'anonymous';
        mockImg.onload = () => {
          // Scale so long edge = EXPORT_PX_LONG_EDGE
          const longEdge = Math.max(mockImg.naturalWidth, mockImg.naturalHeight) || 1;
          const scale = EXPORT_PX_LONG_EDGE / longEdge;
          const W = Math.round(mockImg.naturalWidth * scale);
          const H = Math.round(mockImg.naturalHeight * scale);
          const canvas = document.createElement('canvas');
          canvas.width = W; canvas.height = H;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(mockImg, 0, 0, W, H);

          const finish = () => {
            canvas.toBlob(blob => resolve(blob), 'image/png');
          };

          if (art.fileURL && (art.fileKind === 'raster' || art.fileKind === 'svg')) {
            const artImg = new Image();
            artImg.onload = () => {
              const p = PRINT[side];
              const boxL = (p.x / 100) * W;
              const boxT = (p.y / 100) * H;
              const boxW = (p.w / 100) * W;
              // Derive box height from real inches aspect — matches on-screen preview.
              const boxH = boxW * (p.inH / p.inW);
              const aL = boxL + (art.xPct / 100) * boxW;
              const aT = boxT + (art.yPct / 100) * boxH;
              const aW = (art.wPct / 100) * boxW;
              const aH = (art.hPct / 100) * boxH;

              if (art.rotation) {
                ctx.save();
                ctx.translate(aL + aW / 2, aT + aH / 2);
                ctx.rotate((art.rotation * Math.PI) / 180);
                ctx.drawImage(artImg, -aW / 2, -aH / 2, aW, aH);
                ctx.restore();
              } else {
                ctx.drawImage(artImg, aL, aT, aW, aH);
              }
              finish();
            };
            artImg.onerror = finish;
            artImg.src = art.fileURL;
          } else {
            // Non-visual file — draw a placeholder rectangle so admin can see position
            const p = PRINT[side];
            const boxL = (p.x / 100) * W;
            const boxT = (p.y / 100) * H;
            const boxW = (p.w / 100) * W;
            const boxH = (p.h / 100) * H;
            const aL = boxL + (art.xPct / 100) * boxW;
            const aT = boxT + (art.yPct / 100) * boxH;
            const aW = (art.wPct / 100) * boxW;
            const aH = (art.hPct / 100) * boxH;
            ctx.fillStyle = 'rgba(0, 212, 255, 0.35)';
            ctx.fillRect(aL, aT, aW, aH);
            ctx.strokeStyle = 'rgba(0, 212, 255, 0.9)';
            ctx.lineWidth = 3;
            ctx.strokeRect(aL, aT, aW, aH);
            ctx.fillStyle = '#0a0a0a';
            ctx.font = 'bold 24px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(art.fileName || 'File', aL + aW / 2, aT + aH / 2);
            finish();
          }
        };
        mockImg.onerror = () => resolve(null);
        mockImg.src = url;
      });
    }

    /* ── Submit ───────────────────────────────────────────── */
    function layoutForSide(side) {
      const art = state.sides[side];
      if (!art.file) return null;
      const p = PRINT[side];
      // Convert % → inches (of the real print area)
      const xIn = round2((art.xPct / 100) * p.inW);
      const yIn = round2((art.yPct / 100) * p.inH);
      const wIn = round2((art.wPct / 100) * p.inW);
      const hIn = round2((art.hPct / 100) * p.inH);
      return {
        file: art.fileName,
        rotationDeg: art.rotation,
        position_in: { x: xIn, y: yIn },
        size_in: { w: wIn, h: hIn },
        printArea_in: { w: p.inW, h: p.inH },
      };
    }

    function setAtcProgress(pct, label) {
      $atc.style.setProperty('--tl-progress', `${pct}%`);
      $atcLabel.textContent = label;
    }

    // isFirst=true → carries the artwork files & preview PNGs (big upload).
    // isFirst=false → lightweight metadata-only request (no files), just
    //                 references the first line's artwork. All shirts in an
    //                 order share the same design, so no reason to re-upload.
    function submitOneLine(line, sharedProps, idx, total, isFirst) {
      return new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('id', String(line.variant.id));
        fd.append('quantity', String(line.qty));
        fd.append('properties[Color]', line.color);
        fd.append('properties[Size]', line.size);
        fd.append('properties[Print sides]', sharedProps.printMode);
        fd.append('properties[Order ref]', sharedProps.orderRef);

        if (isFirst) {
          // The one and only line that carries the actual files.
          if (state.sides.front.file) {
            fd.append('properties[Front Artwork]', state.sides.front.file, state.sides.front.fileName);
          }
          if (state.sides.back.file) {
            fd.append('properties[Back Artwork]', state.sides.back.file, state.sides.back.fileName);
          }
          if (sharedProps.frontPreview) {
            fd.append('properties[Front Preview]', sharedProps.frontPreview, sharedProps.frontPreviewName);
          }
          if (sharedProps.backPreview) {
            fd.append('properties[Back Preview]', sharedProps.backPreview, sharedProps.backPreviewName);
          }
          fd.append('properties[_Layout]', sharedProps.layoutJson);
          fd.append('properties[_Notes]', `Artwork attached here — applies to all ${total} line item(s) of this order (ref ${sharedProps.orderRef})`);
        } else {
          // Sibling shirts: no files. Just a merchant note + the CDN URL of the
          // preview that got uploaded on line 1 so the cart thumbnail shows the
          // actual mockup (not the generic product image).
          fd.append('properties[_Notes]', `Uses artwork from the first line item (ref ${sharedProps.orderRef})`);
          if (sharedProps.frontPreviewUrl) {
            fd.append('properties[_Preview URL]', sharedProps.frontPreviewUrl);
          }
          if (sharedProps.backPreviewUrl) {
            fd.append('properties[_Back Preview URL]', sharedProps.backPreviewUrl);
          }
        }

        // Weight progress so the first line gets ~85% of the bar (it has the
        // real upload); remaining lines share the last 15% evenly.
        const firstShare = 0.85;
        const restShare  = total > 1 ? (1 - firstShare) / (total - 1) : 0;

        const xhr = new XMLHttpRequest();
        xhr.open('POST', CART_ADD, true);
        xhr.setRequestHeader('Accept', 'application/javascript');
        xhr.upload.addEventListener('progress', (ev) => {
          if (!ev.lengthComputable) return;
          const pctThis = ev.loaded / ev.total;
          let overallFraction;
          if (isFirst) {
            overallFraction = 0.15 + pctThis * firstShare;
          } else {
            overallFraction = 0.15 + firstShare + ((idx - 1) + pctThis) * restShare;
          }
          const overall = Math.min(99, Math.round(overallFraction * 100));
          const label = isFirst
            ? `Uploading artwork… ${Math.round(pctThis * 100)}%`
            : `Finalizing sizes (${idx} / ${total - 1})…`;
          setAtcProgress(overall, label);
        });
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 400) {
            // Parse the cart-add response so callers can pull the uploaded
            // file's CDN URL out of item.properties (for cart thumbnails).
            let data = null;
            try { data = JSON.parse(xhr.responseText); } catch (_) {}
            resolve(data);
          } else {
            reject(new Error(`Server returned ${xhr.status} on ${line.color} ${line.size}`));
          }
        };
        xhr.onerror = () => reject(new Error(`Network error on ${line.color} ${line.size}`));
        xhr.send(fd);
      });
    }

    async function submit() {
      clearError();
      const lines = matrixLines();
      const orderQty = totalQtyAll();
      if (!lines.length) { showError('Set a quantity for at least one size.'); return; }
      if (!state.sides.front.file && !state.sides.back.file) {
        showError('Upload artwork for at least one side.'); return;
      }
      if (orderQty < MIN_QTY) {
        showError(`Minimum order is ${MIN_QTY} shirts. Add ${MIN_QTY - orderQty} more to check out.`);
        return;
      }
      for (const l of lines) {
        if (!l.variant) {
          showError(`${l.color} ${l.size} isn't available for this print configuration. Check that "One Side" and "Both Sides" variants exist in Shopify.`);
          return;
        }
      }

      $atc.classList.add('is-uploading');
      $atc.disabled = true;
      setAtcProgress(5, 'Preparing preview…');

      const [frontPreview, backPreview] = await Promise.all([
        renderComposite('front'),
        renderComposite('back'),
      ]);
      setAtcProgress(15, 'Uploading to cart…');

      const sides = printSides();
      const one = whichOneSide();
      const modeLabel =
        sides === 'Both Sides' ? 'Front + Back' :
        one === 'front'        ? 'Front only'   :
        one === 'back'         ? 'Back only'    :
        '—';

      // Short human-readable identifier so merchant + customer can see all
      // sibling shirt line items belong to the same "design" in an order.
      const orderRef = 'SHIRT-' + Date.now().toString(36).slice(-5).toUpperCase();

      const sharedProps = {
        frontPreview,
        backPreview,
        frontPreviewName: `front-preview-${orderRef}.png`,
        backPreviewName:  `back-preview-${orderRef}.png`,
        layoutJson: JSON.stringify({
          front: layoutForSide('front'),
          back:  layoutForSide('back'),
          dpi: DPI,
        }),
        printMode: modeLabel,
        orderRef,
      };

      try {
        // Line 1 uploads the files. From its response we grab the CDN URLs
        // that Shopify assigned to Front/Back Preview, then pass them to
        // subsequent lines so their cart thumbnails show the customer's
        // actual mockup instead of the generic product image.
        for (let i = 0; i < lines.length; i++) {
          const resp = await submitOneLine(lines[i], sharedProps, i, lines.length, i === 0);
          if (i === 0 && resp && resp.properties) {
            const fp = resp.properties['Front Preview'];
            const bp = resp.properties['Back Preview'];
            if (typeof fp === 'string' && fp.length) sharedProps.frontPreviewUrl = fp;
            if (typeof bp === 'string' && bp.length) sharedProps.backPreviewUrl  = bp;
          }
        }
        setAtcProgress(100, 'Added to cart → going to checkout');
        window.location.href = CART_URL;
      } catch (err) {
        onSubmitFail(err.message + '. Please try again.');
      }
    }

    function onSubmitFail(msg) {
      $atc.classList.remove('is-uploading');
      $atc.disabled = false;
      $atcLabel.textContent = 'Add to Cart';
      showError(msg);
    }

    function showError(m)   { if ($err) { $err.textContent = m; $err.hidden = false; } }
    function clearError()   { if ($err) { $err.hidden = true; $err.textContent = ''; } }
    function showUploadErr(m) { if ($upErr) { $upErr.textContent = m; $upErr.hidden = false; } }
    function clearUploadErr() { if ($upErr) { $upErr.hidden = true; $upErr.textContent = ''; } }

    /* ── Event wiring ─────────────────────────────────────── */
    // Side tabs on the stage
    $sideTabs.forEach(btn => btn.addEventListener('click', () => {
      state.activeSide = btn.dataset.tlShirtSide;
      paintAll();
    }));
    // Upload tabs mirror the same
    $uploadTabs.forEach(btn => btn.addEventListener('click', () => {
      state.activeSide = btn.dataset.tlShirtUploadTab;
      paintAll();
    }));

    // Swatches (delegation) — click to view that color's matrix + swap mockup
    $swatches?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tl-shirt-color]'); if (!btn) return;
      state.activeColor = btn.dataset.tlShirtColor;
      paintAll();
    });

    // Size matrix — stepper buttons + direct qty input
    $sizes?.addEventListener('click', (e) => {
      const stepBtn = e.target.closest('[data-tl-shirt-matrix-step]');
      if (!stepBtn) return;
      const row = stepBtn.closest('[data-size]');
      if (!row || row.classList.contains('is-unavailable')) return;
      const size = row.dataset.size;
      const dir = parseInt(stepBtn.dataset.tlShirtMatrixStep, 10) || 1;
      const current = getMatrixQty(state.activeColor, size);
      setMatrixQty(state.activeColor, size, Math.max(0, current + dir));
      paintAll();
    });
    $sizes?.addEventListener('input', (e) => {
      const inp = e.target.closest('[data-tl-shirt-matrix-input]');
      if (!inp) return;
      const row = inp.closest('[data-size]');
      if (!row) return;
      const size = row.dataset.size;
      const v = parseInt(inp.value, 10);
      setMatrixQty(state.activeColor, size, Number.isFinite(v) && v > 0 ? v : 0);
      // Repaint everything except the input itself (to preserve focus + caret)
      paintSwatches();
      paintOrderList();
      paintSummary();
    });

    // Order list — remove a color+size line
    $orderList?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tl-shirt-remove-line]');
      if (!btn) return;
      const [color, size] = btn.dataset.tlShirtRemoveLine.split('|');
      setMatrixQty(color, size, 0);
      paintAll();
    });

    // File input
    $fileInput?.addEventListener('change', (e) => {
      const f = e.target.files?.[0]; if (f) attachFile(f);
      e.target.value = '';
    });
    // The <label> already triggers the input; ensure the drop zone click works too.
    $drop?.addEventListener('click', (e) => {
      // Prevent double-fire if user clicked one of the inner action buttons.
      if (e.target.closest('button')) return;
      $fileInput?.click();
    });
    $upReplace?.addEventListener('click', (e) => { e.preventDefault(); $fileInput?.click(); });
    $upRemove?.addEventListener('click',  (e) => { e.preventDefault(); removeArt(); });

    // Drag & drop onto upload zone
    ['dragenter', 'dragover'].forEach(ev => $drop?.addEventListener(ev, (e) => {
      e.preventDefault(); e.stopPropagation(); $drop.classList.add('is-dragging');
    }));
    ['dragleave', 'drop'].forEach(ev => $drop?.addEventListener(ev, (e) => {
      e.preventDefault(); e.stopPropagation(); $drop.classList.remove('is-dragging');
    }));
    $drop?.addEventListener('drop', (e) => {
      const f = e.dataTransfer?.files?.[0]; if (f) attachFile(f);
    });

    // Drag & drop directly onto the shirt stage
    ['dragenter', 'dragover'].forEach(ev => $stage?.addEventListener(ev, (e) => {
      e.preventDefault(); e.stopPropagation();
    }));
    $stage?.addEventListener('drop', (e) => {
      e.preventDefault(); e.stopPropagation();
      const f = e.dataTransfer?.files?.[0]; if (f) attachFile(f);
    });

    // Toolbar
    $tools.fit?.addEventListener('click', () => {
      const a = activeArt(); if (!a.file) return;
      fitArtToBox(a); positionArt();
    });
    $tools.center?.addEventListener('click', centerArt);
    $tools.rotate?.addEventListener('click', rotateArt);
    $tools.remove?.addEventListener('click', removeArt);

    // Pointer interactions on the artwork
    $art?.addEventListener('pointerdown', onPointerDown);
    $art?.addEventListener('pointermove', onPointerMove);
    $art?.addEventListener('pointerup',   onPointerUp);
    $art?.addEventListener('pointercancel', onPointerUp);

    // Upsell CTA — switch canvas + upload tabs to the empty side
    $upsellCta?.addEventListener('click', () => {
      const target = $upsellCta.dataset.tlUpsellTarget;
      if (target !== 'front' && target !== 'back') return;
      state.activeSide = target;
      paintAll();
    });

    // ATC
    $atc?.addEventListener('click', submit);

    // Repaint art on window resize (mockup box changes size)
    window.addEventListener('resize', () => { if (activeArt().file) positionArt(); });

    // Cart cross-tab sync
    function refreshCart() {
      return fetchExistingCartQty().then(q => {
        if (q !== state.existingCartQty) { state.existingCartQty = q; paintSummary(); }
      });
    }
    ['cart:update', 'cart:refresh', 'cart:change'].forEach(evt =>
      document.addEventListener(evt, refreshCart));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshCart();
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });

    /* ── Init ─────────────────────────────────────────────── */
    refreshCart();
    paintAll();
  }

  function safeJSON(str, fallback) {
    try { return JSON.parse(str || 'null') ?? fallback; }
    catch (e) { return fallback; }
  }

  function init() { document.querySelectorAll('[data-tl-shirt]').forEach(initBuilder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
